import React, {
  Children,
  cloneElement,
  forwardRef,
  ForwardRefExoticComponent,
  isValidElement,
  MouseEvent,
  ReactElement,
  ReactNode,
  RefAttributes,
  useCallback,
  useEffect,
  useRef,
} from 'react'
import ReactDOM from 'react-dom'
import { useKey } from 'react-use'

import { PORTAL_CLASS_NAME } from '../../constants/portalClassName'
import { useComponentToggle } from '../../hooks/useComponentToggle'
import { isTopmostPortalElement } from '../../utils/isTopmostPortalElement'
import { transitionToMiliseconds } from '../../utils/transitionToMiliseconds'
import { isAnyDropdownOpen } from '../Dropdown'
import { MODAL_RENDER_TRANSITION_DURATION } from './constants/modalRenderTransitionDuration'
import { useModal } from './context/hooks/useModal'
import { ModalBody } from './elements/ModalBody'
import { ModalFooter } from './elements/ModalFooter'
import { ModalHeader } from './elements/ModalHeader'
import { ModalNavigation } from './elements/ModalNavigation'
import { useFocusOnFirstInput } from './hooks/useFocusOnFirstInput'
import * as Styled from './styles'
import { Size } from './types/size'

export interface ModalProps {
  autofocusOnInput?: boolean
  children?: ReactNode
  closable?: boolean
  forceRender?: boolean
  id: string
  size?: Size
  className?: string
}

type ModalType = ForwardRefExoticComponent<ModalProps & RefAttributes<HTMLDivElement>> & {
  Body: typeof ModalBody
  Footer: typeof ModalFooter
  Header: typeof ModalHeader
  Navigation: typeof ModalNavigation
}

export const Modal = forwardRef(
  (
    { autofocusOnInput, id, className, closable = true, children, forceRender, size = 'm' },
    forwardedRef,
  ): ReactElement => {
    const { isOpen, isLastOpened, close, open } = useModal(id)
    const { isVisible, isRendered } = useComponentToggle({
      isOpen,
      animationTime: transitionToMiliseconds(MODAL_RENDER_TRANSITION_DURATION),
    })
    const modalRef = useRef<HTMLDivElement>(null)

    useFocusOnFirstInput(modalRef.current, autofocusOnInput)

    useEffect(() => {
      if (forceRender) {
        open()
      }
    }, [forceRender, open])

    const handleWrapperClick = useCallback(
      (event: MouseEvent<HTMLElement>) => {
        event.stopPropagation()
        const clickedElement = event.target
        const clickedInWrapper = clickedElement instanceof Node && event.currentTarget.contains(clickedElement)
        const clickedInModal = clickedElement instanceof Node && Boolean(modalRef.current?.contains(clickedElement))

        /*
          A click inside a React portal still reaches this handler: portals bubble through the
          React tree, and the portal's target is outside this wrapper's DOM. That portal is a
          descendant of the modal, so only a click on the backdrop itself dismisses it.
        */
        if (isLastOpened && closable && clickedInWrapper && !clickedInModal) {
          close()
        }
      },
      [closable, close, isLastOpened],
    )

    const handleEscapePress = useCallback(() => {
      const wrapperElement = modalRef.current?.closest(`.${PORTAL_CLASS_NAME}`) ?? null

      if (closable && isLastOpened && !isAnyDropdownOpen() && isTopmostPortalElement(wrapperElement)) {
        close()
      }
    }, [closable, close, isLastOpened])

    useKey('Escape', handleEscapePress, { target: window }, [closable, isLastOpened, close])

    return ReactDOM.createPortal(
      <>
        {isRendered && (
          <>
            <Styled.ModalWrapper
              ref={forwardedRef}
              className={PORTAL_CLASS_NAME}
              onClick={handleWrapperClick}
              size={size}
              withOverlay={isVisible && isLastOpened}
            >
              <Styled.Modal ref={modalRef} size={size} visible={isVisible} className={className}>
                {Children.map(children, (child) => {
                  if (isValidElement<Pick<ModalProps, 'id' | 'closable' | 'size'>>(child)) {
                    return cloneElement(child, { closable, id, size })
                  }

                  return child
                })}
              </Styled.Modal>
            </Styled.ModalWrapper>
          </>
        )}
      </>,
      document.body,
    )
  },
) as ModalType

Modal.Header = ModalHeader
Modal.Navigation = ModalNavigation
Modal.Body = ModalBody
Modal.Footer = ModalFooter
